import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { user } from 'ampersand-app'
import useMediaQuery from '@mui/material/useMediaQuery'

import { DIST_PRODUCT } from '@/enums/dist-products'

import { menuItemsCenter, menuItemsFooter } from '@/modules/layout/components/menu/config'
import LogoSvg from '@assets/images/logo/svg/logo_whiteblue.svg'
import ArrowIcon from '@assets/images/onboarding/arrow.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import ProfileSection from '@/modules/layout/components/submenu/profie-section/profile-section'
import SubmenuItem from '../item/submenu-item'
import { SubmenuFooter } from '../footer/submenu-footer'

import { userLoggedOut } from 'redux/ducks/global'

import { fetchMenuItems } from 'redux/ducks/apps'
import { USER_RIGHTS, SUBSCRIPTION_MODULES } from '@/modules/base/util/constants'
import { isObject } from 'lodash'
import { checkAccessRights } from '@/modules'
import { useNavigate } from 'react-router'

const isMeinbuero = ENV.product === DIST_PRODUCT.meinbuero

const useStyles = makeStyles(theme => ({
  container: ({ submenuOnScreen }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: 200,
    top: 0,
    background: 'var(--nav-submenu-background-color)',
    boxShadow: '3px 0 5px -2px rgba(60, 61, 70, 0.14)',
    paddingLeft: 30,
    zIndex: 25,
    position: 'absolute',
    left: submenuOnScreen ? 80 : -110,
    transition: 'all 0.3s',
    overflow: 'auto',
  }),
  '@media only screen and (min-width: 1300px)': {
    container: () => ({
      left: 80,
    }),
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    height: '31%',
    flexDirection: 'column',
  },
  logoWrapper: ({ isMeinbuero }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    cursor: 'pointer',
    paddingTop: isMeinbuero ? 27 : 22,
  }),
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
  },
  items: {
    display: 'inline-flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
  },
  'item-visible': {
    animation: 'fade-in 0.7s',
    animationFillMode: 'both',
  },
  title: {
    color: 'var(--nav-submenu-title-color)',
    fontSize: 18,
    paddingBottom: 30,
  },
  logout: {
    display: 'flex',
    alignItems: 'center',
    color: 'var(--nav-submenu-item-logout-color)',
    fontWeight: 600,
    height: 55,
    width: '100%',
    fontSize: 13,
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--nav-submenu-item-logout-color-hover)',
    },
  },
  imprint: {
    marginBottom: 20,
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    fontSize: '0.875rem',
  },
  icon: {
    display: 'none',
  },
  [theme.devices.mobile]: {
    container: () => ({
      left: 0,
      width: '100%',
      background: theme.navBackgroundColor,
      padding: '0 20px',
    }),
    header: {
      display: 'none',
    },
    title: {
      padding: '30px 0',
      fontSize: theme.fontSizes.smedium,
      borderTop: theme.border,
      borderBottom: theme.border,
      borderColor: `${theme.transparentBorder} !important`,
      width: '100%',
    },
    icon: {
      display: 'inline-flex',
      transform: 'rotate(180deg)',
      marginRight: 13,
    },
    items: {
      marginLeft: 30,
      '& .submenu-item__container': {
        marginTop: 30,
        '& .submenu-item__link, & .submenu-item__notallowed  ': {
          fontSize: 14,
          height: 'auto',
        },
      },
    },
    imprint: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px 0',
      fontSize: theme.fontSizes.default,
      borderTop: theme.border,
      borderColor: theme.transparentBorder,
    },
  },
  '@keyframes slide-left': {
    from: { width: 0 },
    to: { width: 200 },
  },
  '@keyframes fade-in': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
}))

const SubmenuBar = ({ toggleSubMenuBar }) => {
  const navigate = useNavigate()
  const {
    menu: { main: { activeItem, hoveredItem, submenuOnScreen } },
    user: { info: userInfo },
    apps: { menuItems: appsMenuItems, menuItemsAlreadyFetched },
    permission: { moduleAccess: { [SUBSCRIPTION_MODULES.MARKETPLACE]: marketplaceAccess } },
    tenant: { info: tenantInfo },
    subscription: { details: { loading } },
  } = useSelector(state => state)

  const styles = useStyles({ submenuOnScreen, isMeinbuero }, [submenuOnScreen])
  const dispatch = useDispatch()
  const narrowScreen = useMediaQuery('only screen and (max-width:1300px)')

  useEffect(() => {
    const hasAppRights = checkAccessRights(USER_RIGHTS.APPS.READ, userInfo)
    if (marketplaceAccess && !menuItemsAlreadyFetched && hasAppRights) dispatch(fetchMenuItems())
  }, [userInfo?.roles, menuItemsAlreadyFetched])

  const onClick = () => navigate('/dashboard')

  const [menuItem] = (narrowScreen && hoveredItem && hoveredItem.split('/')) || activeItem.split('/')
  const activeMenuItem = !loading && isObject(userInfo.roles) && Object.keys(userInfo.roles).length
    ? [...menuItemsCenter(appsMenuItems), ...menuItemsFooter(userInfo, tenantInfo)].find(item => item.name === menuItem)
    : null
  if (!activeMenuItem) return <div />

  const submenuItems = activeMenuItem.submenuItems.map((submenuItem, index) => <SubmenuItem key={index} parentName={activeMenuItem.name} {...submenuItem} />)

  if (activeMenuItem.name === 'settings') {
    submenuItems.push(
      <div
        key="logout"
        data-ref="userLogout"
        className={styles.logout}
        onClick={() => {
          dispatch(userLoggedOut())
          user.logout(true)
        }}>
        <span>Abmelden</span>
      </div>,
    )
  }

  return (
    <div className={styles.container} id='submenu-bar'>
      <div className={styles.header}>
        <div className={styles.logoWrapper} onClick={onClick} id='menuHeaderLogo'>
          <SVGIcon svg={LogoSvg} width="150px" height="auto" />
        </div>
        <ProfileSection />
      </div>
      <div className={styles.content}>
        <span className={styles.title} onClick={toggleSubMenuBar}>
          <SVGIcon svg={ArrowIcon} width="16px" height="14px" className={styles.icon} />
          {activeMenuItem.title}</span>
        <div className={styles.items}>{submenuItems}</div>
      </div>
      <div className={styles.imprint}>
        <SubmenuFooter />
      </div>
    </div>
  )
}

export default SubmenuBar
